import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaHospital,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaAmbulance,
  FaUserMd,
  FaStethoscope,
  FaHeartbeat,
  FaStar,
  FaCalendarCheck,
  FaCheckCircle,
  FaBolt,
  FaUserFriends,
  FaBed,
  FaMicroscope,
  FaProcedures,
  FaMedkit,
  FaWifi,
  FaParking,
  FaWheelchair,
  FaCoffee,
  FaPhone,
  FaCalendarAlt,
  FaGraduationCap,
  FaBriefcaseMedical,
  FaExclamationTriangle,
  FaArrowRight,
  FaTrophy,
  FaAward,
  FaUsers,
  FaChevronRight
} from 'react-icons/fa';
import { database } from '../firebase';
import { ref as dbRef, get, set } from 'firebase/database';

const Hospital = () => {
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    department: '',
    symptoms: ''
  });

  // State for Firebase data
  const [hospitalData, setHospitalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch hospital data from Firebase
  useEffect(() => {
    const fetchHospitalData = async () => {
      try {
        setLoading(true);
        const hospitalRef = dbRef(database, 'Dr/hospital');
        const snapshot = await get(hospitalRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          setHospitalData(data);
        } else {
          setError('No hospital data found');
        }
      } catch (err) {
        console.error('Error fetching hospital data:', err);
        setError('Failed to load hospital data');
      } finally {
        setLoading(false);
      }
    };

    fetchHospitalData();
  }, []);

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const icons = {
      FaAmbulance: <FaAmbulance />,
      FaUserFriends: <FaUserFriends />,
      FaMicroscope: <FaMicroscope />,
      FaMedkit: <FaMedkit />,
      FaProcedures: <FaProcedures />,
      FaBed: <FaBed />,
      FaHospital: <FaHospital />,
      FaWifi: <FaWifi />,
      FaParking: <FaParking />,
      FaWheelchair: <FaWheelchair />,
      FaCoffee: <FaCoffee />,
      FaStethoscope: <FaStethoscope />,
      FaBriefcaseMedical: <FaBriefcaseMedical />
    };
    return icons[iconName] || <FaMedkit />;
  };

  // Default fallback data
  const defaultHospitalInfo = {
    name: 'City Health Hospital & Research Center',
    tagline: 'Excellence in Healthcare with Compassion',
    contact: '+91 98765 43210',
    emergency: '+91 98765 43210',
    email: 'contact@cityhealthhospital.com',
    address: '123 Healthcare Street, Medical District, Mumbai, Maharashtra - 400001',
    workingHours: {
      mondayToFriday: '8:00 AM - 10:00 PM',
      saturday: '9:00 AM - 8:00 PM',
      sunday: '10:00 AM - 6:00 PM'
    },
    emergencyAvailable: true,
    about: 'City Health Hospital & Research Center is a state-of-the-art multi-specialty healthcare facility.',
    vision: 'To be the leading healthcare provider offering accessible, affordable, and advanced medical care.',
  };


  // Use Firebase data or fallback to default
  const hospitalInfo = hospitalData || defaultHospitalInfo;
  const diseasesTreated = hospitalData?.diseasesTreated || [];
  const doctors = hospitalData?.doctors || [];
  const services = hospitalData?.services || [];
  // const facilities = hospitalData?.facilities || defaultFacilities;

  // State for appointment submission feedback
  const [appointmentSuccess, setAppointmentSuccess] = useState(false);
  const [appointmentError, setAppointmentError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    // Name validation - only letters
    if (!appointmentForm.name.trim()) {
      errors.name = 'Full name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(appointmentForm.name)) {
      errors.name = 'Name should contain only letters';
    } else if (appointmentForm.name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }
    
    // Phone validation - only numbers, exactly 10 digits
    if (!appointmentForm.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(appointmentForm.phone)) {
      errors.phone = 'Phone number must be exactly 10 digits';
    }
    
    // Email validation
    if (!appointmentForm.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(appointmentForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Date validation
    if (!appointmentForm.date) {
      errors.date = 'Appointment date is required';
    } else {
      const selectedDate = new Date(appointmentForm.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        errors.date = 'Appointment date cannot be in the past';
      }
    }
    
    // Department validation
    if (!appointmentForm.department) {
      errors.department = 'Please select a disease/department';
    }
    
    return errors;
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const appointmentData = {
        name: appointmentForm.name,
        phone: appointmentForm.phone,
        email: appointmentForm.email,
        date: appointmentForm.date,
        department: appointmentForm.department,
        symptoms: appointmentForm.symptoms,
        timestamp: new Date().toISOString(),
        status: "Pending"
      };

      const phone = appointmentForm.phone;

      // Reference to this phone's appointments
      const phoneAppointmentsRef = dbRef(database, `Dr/appointments/${phone}`);

      // Get existing appointments count
      const snapshot = await get(phoneAppointmentsRef);
      let nextIndex = 1;

      if (snapshot.exists()) {
        nextIndex = snapshot.size + 1;
      }

      // Store appointment as 1,2,3...
      const newAppointmentRef = dbRef(
        database,
        `Dr/appointments/${phone}/${nextIndex}`
      );

      await set(newAppointmentRef, appointmentData);

      setAppointmentSuccess(true);
      setAppointmentError("");

      setAppointmentForm({
        name: "",
        phone: "",
        email: "",
        date: "",
        department: "",
        symptoms: ""
      });

      setTimeout(() => {
        setShowAppointmentForm(false);
        setAppointmentSuccess(false);
      }, 2000);

    } catch (error) {
      console.error("Error submitting appointment:", error);
      setAppointmentError("Failed to submit appointment. Please try again.");
      setAppointmentSuccess(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear previous error for this field
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));
    
    // Apply validation based on field type
    let validatedValue = value;
    
    if (name === 'name') {
      // Only allow letters and spaces
      validatedValue = value.replace(/[^a-zA-Z\s]/g, '');
    } else if (name === 'phone') {
      // Only allow numbers, max 10 digits
      validatedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'email') {
      // Keep email as is, will validate on submit
      validatedValue = value;
    }
    
    setAppointmentForm(prev => ({
      ...prev,
      [name]: validatedValue
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <FaHospital className="text-6xl text-teal-600 mx-auto mb-4 animate-pulse" />
          <p className="text-xl text-gray-600">Loading hospital information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="text-6xl text-red-500 mx-auto mb-4" />
          <p className="text-xl text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Hospital Image */}
            {hospitalData?.image && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="lg:w-1/4"
              >
                <img
                  src={hospitalData.image}
                  alt={hospitalInfo.name}
                  className="w-full h-64 object-cover rounded-2xl shadow-2xl border-4 border-white/20"
                />
              </motion.div>
            )}

            <div className={hospitalData?.image ? "lg:w-2/5" : "lg:w-2/3"}>
              <motion.h1 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl lg:text-5xl font-bold mb-4"
              >
                {hospitalInfo.name}
              </motion.h1>
              <p className="text-xl opacity-90 mb-6">{hospitalInfo.tagline || 'Excellence in Healthcare with Compassion'}</p>
              
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAppointmentForm(true)}
                  className="bg-white text-teal-600 px-6 py-3 rounded-xl font-bold hover:shadow-xl transition-shadow flex items-center"
                >
                  <FaCalendarCheck className="mr-2" />
                  Book Appointment
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-red-500 text-white px-6 py-3 rounded-xl font-bold hover:shadow-xl transition-shadow flex items-center"
                >
                  <FaAmbulance className="mr-2" />
                  Emergency: {hospitalInfo.contactNumber}
                </motion.button>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 lg:p-8">
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <FaPhoneAlt className="mr-3 text-teal-300" />
                  <div>
                    <p className="text-sm opacity-80">Phone</p>
                    <p className="font-bold">{hospitalInfo.contactNumber}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaEnvelope className="mr-3 text-teal-300" />
                  <div>
                    <p className="text-sm opacity-80">Email</p>
                    <p className="font-bold">{hospitalInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaMapMarkerAlt className="mr-3 text-teal-300" />
                  <div>
                    <p className="text-sm opacity-80">Address</p>
                    <p className="font-bold">{hospitalInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Working Hours & Emergency */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaClock className="mr-3 text-teal-600" />
              Working Hours
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-700">Monday - Friday</span>
                <span className="font-bold text-teal-600">{hospitalInfo.workingHours.mondayToFriday}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-700">Saturday</span>
                <span className="font-bold text-teal-600">{hospitalInfo.workingHours.saturday}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-700">Sunday</span>
                <span className="font-bold text-teal-600">{hospitalInfo.workingHours.sunday}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`bg-gradient-to-br rounded-2xl shadow-lg p-6 ${
              hospitalInfo.emergencyAvailable 
                ? 'from-red-500 to-pink-600 text-white' 
                : 'from-gray-500 to-gray-700 text-white'
            }`}
          >
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <FaExclamationTriangle className="mr-3" />
              Emergency Services
            </h2>
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-3xl font-bold">{hospitalInfo.contactNumber}</p>
                <p className="opacity-90">Emergency Contact</p>
              </div>
              <div className="text-4xl">
                <FaBolt />
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center justify-center">
                <FaCheckCircle className="mr-2 text-xl" />
                <span className="font-bold text-lg">
                  {hospitalInfo.emergencyAvailable ? '24/7 Available' : 'Currently Unavailable'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Hospital Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <FaHospital className="mr-3 text-teal-600" />
            About Our Hospital
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-3">Hospital Overview</h3>
              <p className="text-gray-600 leading-relaxed">{hospitalInfo.about}</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6">
                <h4 className="font-bold text-teal-700 mb-3 flex items-center">
                  <FaTrophy className="mr-2" />
                  Our Vision
                </h4>
                <p className="text-gray-700 italic">"{hospitalInfo.vision}"</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Diseases Treated */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <FaStethoscope className="mr-3 text-teal-600" />
            Diseases & Conditions Treated
          </h2>
          
          {diseasesTreated.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {diseasesTreated.map((disease) => (
                <motion.div
                  key={disease.id}
                  whileHover={{ y: -5 }}
                  className="border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start mb-4">
                    <span className="text-3xl mr-3">🩺</span>
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg">{disease.name}</h3>
                      {disease.description && (
                        <p className="text-gray-600 text-sm mt-2">{disease.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <button className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center mt-4">
                    Consult Specialist
                    <FaChevronRight className="ml-1" />
                  </button>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FaStethoscope className="text-5xl mx-auto mb-4 opacity-30" />
              <p>No diseases information available at the moment.</p>
            </div>
          )}
        </motion.div>

        {/* Doctor Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <FaUserMd className="mr-3 text-teal-600" />
            Our Expert Doctors
          </h2>
          
          {doctors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {doctors.map((doctor) => (
                <motion.div
                  key={doctor.id}
                  whileHover={{ y: -5 }}
                  className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all"
                >
                  <div className="relative">
                    <img 
                      src={doctor.image || 'https://via.placeholder.com/400x300?text=Doctor'} 
                      alt={doctor.name}
                      className="w-full h-48 object-cover"
                    />
                    {doctor.rating && (
                      <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full flex items-center text-sm">
                        <FaStar className="mr-1" /> {doctor.rating}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-5">
                    <h3 className="font-bold text-gray-800 text-lg mb-2">{doctor.name}</h3>
                    <p className="text-gray-600 text-sm mb-3 flex items-center">
                      <FaGraduationCap className="mr-2 text-gray-400" />
                      {doctor.qualification}
                    </p>
                    
                    {doctor.specialization && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Specialization:</p>
                        <div className="flex flex-wrap gap-1">
                          {(typeof doctor.specialization === 'string' 
                            ? [doctor.specialization] 
                            : doctor.specialization || []
                          ).map((spec, idx) => (
                            <span key={idx} className="bg-teal-50 text-teal-700 px-2 py-1 rounded text-xs">
                              {spec}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      {doctor.experience && (
                        <div className="flex items-center">
                          <FaBriefcaseMedical className="mr-2 text-gray-400" />
                          <span>{doctor.experience} Experience</span>
                        </div>
                      )}
                      {doctor.availability && (
                        <div className="flex items-center">
                          <FaCalendarAlt className="mr-2 text-gray-400" />
                          <span>Available: {Array.isArray(doctor.availability) ? doctor.availability.join(', ') : doctor.availability}</span>
                        </div>
                      )}
                      {doctor.schedule && (
                        <div className="flex items-center">
                          <FaClock className="mr-2 text-gray-400" />
                          <span>Timing: {doctor.schedule}</span>
                        </div>
                      )}
                      {doctor.patients && (
                        <div className="flex items-center">
                          <FaUsers className="mr-2 text-gray-400" />
                          <span>{doctor.patients} Patients</span>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => {
                        const spec = Array.isArray(doctor.specialization) 
                          ? doctor.specialization[0] 
                          : doctor.specialization || 'General';
                        setAppointmentForm(prev => ({ ...prev, department: spec }));
                        setShowAppointmentForm(true);
                      }}
                      className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-2 rounded-lg hover:shadow-lg transition-all font-medium"
                    >
                      Book Appointment
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FaUserMd className="text-5xl mx-auto mb-4 opacity-30" />
              <p>No doctors information available at the moment.</p>
            </div>
          )}
        </motion.div>

        {/* Services & Facilities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaMedkit className="mr-3 text-teal-600" />
              Our Services
            </h2>
            
            {services.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {services.map((service) => (
                  <div key={service.id} className="text-center p-4 border border-gray-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all">
                    <div className="text-3xl text-teal-500 mb-3">
                      {typeof service.icon === 'string' ? getIconComponent(service.icon) : service.icon}
                    </div>
                    <h3 className="font-bold text-gray-800 mb-1">{service.name}</h3>
                    {service.description && (
                      <p className="text-gray-600 text-sm">{service.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaMedkit className="text-5xl mx-auto mb-4 opacity-30" />
                <p>No services information available.</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Contact & Appointment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl shadow-2xl p-8 text-white"
        >
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="lg:w-2/3 mb-8 lg:mb-0">
              <h2 className="text-3xl font-bold mb-4">Contact & Appointment</h2>
              <p className="text-xl opacity-90 mb-6">Book your appointment today and experience quality healthcare</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                  <div className="flex items-center mb-3">
                    <FaPhone className="text-2xl mr-3" />
                    <div>
                      <p className="text-sm opacity-80">Appointment Contact</p>
                      <p className="text-xl font-bold">
                        {hospitalData?.appointmentSettings?.contactNumber || hospitalInfo.contactNumber}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm opacity-80">Call us for appointment booking and inquiries</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                  <div className="flex items-center mb-3">
                    <FaEnvelope className="text-2xl mr-3" />
                    <div>
                      <p className="text-sm opacity-80">Appointment Email</p>
                      <p className="text-xl font-bold">
                        {hospitalData?.appointmentSettings?.email || hospitalInfo.email}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm opacity-80">Email us for appointment requests and information</p>
                </div>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAppointmentForm(true)}
              className="bg-white text-teal-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-shadow flex items-center"
            >
              <FaCalendarCheck className="mr-3 text-xl" />
              Book Appointment Now
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Appointment Form Modal */}
      {showAppointmentForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl max-w-md w-full p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Book Appointment</h3>
              <button
                onClick={() => setShowAppointmentForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {/* Success and Error Messages */}
            {appointmentSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
                <FaCheckCircle className="text-green-500 text-xl mr-3" />
                <p className="text-green-800 font-medium">Appointment submitted successfully! We will contact you soon.</p>
              </div>
            )}
            
            {appointmentError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
                <FaExclamationTriangle className="text-red-500 text-xl mr-3" />
                <p className="text-red-800 font-medium">{appointmentError}</p>
              </div>
            )}
            
            <form onSubmit={handleAppointmentSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={appointmentForm.name}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    formErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={appointmentForm.phone}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      formErrors.phone ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Phone number"
                  />
                  {formErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={appointmentForm.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      formErrors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Email address"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={appointmentForm.date}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      formErrors.date ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.date && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.date}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Disease*</label>
                  <select
                    name="department"
                    value={appointmentForm.department}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      formErrors.department ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Disease </option>
                    {diseasesTreated.map((disease) => (
                      <option key={disease.id} value={disease.name}>
                        {disease.name}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {formErrors.department && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.department}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Symptoms (Optional)</label>
                <textarea
                  name="symptoms"
                  value={appointmentForm.symptoms}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                  placeholder="Briefly describe your symptoms"
                ></textarea>
              </div>
              
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-shadow"
              >
                Submit Appointment Request
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Hospital;